import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CrudForm from "../../../components/dashboard/form/CrudForm";
import CrudTable from "../../../components/dashboard/table/CrudTable";
import DashboardWrapper from "../../../components/DashboardWrapper";
import productService from "../../../services/productService";
import Swal from "sweetalert2";

export default function ImageGallery() {
  const { id: productId } = useParams();
  const navigate = useNavigate();

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // ✅ Reusable Alert Function
  const showAlert = (title, text, icon = "success") => {
    return Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      confirmButtonColor: "#FB6C22",
    });
  };

  // ✅ Fetch images
  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      try {
        const res = await productService.getProductImages(productId);
        const raw = res.data?.product?.product_image_galleries || [];

        const formattedImages = raw.map((img) => ({
          ...img,
          image: img.image
            ? img.image.startsWith("http")
              ? img.image
              : `${process.env.REACT_APP_BASE_IMAGE_URL}${img.image}`
            : "",
        }));

        setImages(formattedImages);
      } catch (error) {
        console.error("Fetch error:", error);
        setErrors({ fetch: error.message || "Failed to fetch images" });
        showAlert("Error", "Failed to fetch images.", "error");
      } finally {
        setLoading(false);
      }
    };

    if (productId) fetchImages();
  }, [productId]);

  // ✅ Upload multiple images
  const handleUpload = async (formData) => {
    const files = formData.image;

    if (!files || files.length === 0) {
      return showAlert("Error", "Please select one or more images.", "error");
    }

    try {
      const uploadData = new FormData();
      uploadData.append("product_id", productId);

      for (const file of files) {
        uploadData.append("images[]", file);
      }

      await productService.uploadProductImages(uploadData);

      await showAlert("Success", "Images uploaded successfully.");
      window.location.reload();
    } catch (error) {
      console.log(error.response?.data);

      showAlert(
        "Error",
        error.response?.data?.message ||
          JSON.stringify(error.response?.data?.errors) ||
          "Upload failed.",
        "error"
      );
    }
  };

  // ✅ Delete image
  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This image will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#FB6C22",
      cancelButtonColor: "#d33",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await productService.deleteProductImage(id);

          await showAlert("Success", "Image deleted successfully.");
          setImages((prev) => prev.filter((img) => img.id !== id));
        } catch (error) {
          console.error("Delete failed:", error);
          showAlert("Error", "Failed to delete image.", "error");
        }
      }
    });
  };

  return (
    <DashboardWrapper>
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <button
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>

        {/* Upload Form */}
        <div className="card p-4 mb-4">
          <CrudForm
            onSubmit={handleUpload}
            initialData={{ image: [] }}
            allowedFields={["image"]}
            fileFields={[{ name: "image", multiple: true }]}
          />
        </div>

        {/* Loading */}
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "200px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : errors.fetch ? (
          <div className="text-danger">{errors.fetch}</div>
        ) : (
          <div className="card p-4">
            <h5 className="mb-3">All Images</h5>
            <CrudTable
              data={images}
              columns={["id", "image"]}
              onDelete={handleDelete}
              onEdit={() => {}}
              onToggleStatus={() => {}}
            />
          </div>
        )}
      </div>
    </DashboardWrapper>
  );
}
